import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { SectionHeader } from "@/ds/custom/SectionHeader";
import { Selector } from "@/ds/custom/Selector";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { ListItem } from "@/features/common/components/dataview/ListItem";
import { DataView } from "@/features/common/components/dataview/DataView";
import { GalleryItem, GalleryItemProperty } from "@/features/common/components/dataview/GalleryItem";
import { SmallProductDisplay } from "@/features/common/components/SmallProductDisplay";
import { useEvItemBreakdownCategoryVariants, type EvItemBreakdownCategoryVariantResponseModel } from "@/features/ev/api/useEvItemBreakdownCategoryVariants";
import { EvCategoryVariantSortOptions } from "@/features/ev/EvCategoryVariantSort";
import type { EvSetType } from "@/features/ev/EvSetType";
import { useUserPreferencesState } from "@/lib/state/UserPreferencesState";
import { SortAscIcon } from "lucide-react";
import { useCallback, useMemo, type FC, type RefObject } from "react";

export interface EvCategoryVariantsProps {
	evSetType: EvSetType;
	category: string;
	categoryName: string;
	scrollRef: RefObject<HTMLDivElement | null>;
	onSelectCategoryVariant: (categoryVariant: EvItemBreakdownCategoryVariantResponseModel) => void;
}

export const EvCategoryVariants: FC<EvCategoryVariantsProps> = props => {
	const {
		evSetType,
		category,
		categoryName,
		scrollRef,
		onSelectCategoryVariant
	} = props;

	const { state, dispatchSetEvCategoryVariantSort } = useUserPreferencesState();
	const categoryVariants = useEvItemBreakdownCategoryVariants(evSetType, category, state.evCategoryVariantSort);

	const additionalProps = useMemo(() => ({
		onSelectCategoryVariant
	}), [onSelectCategoryVariant]);

	return (
		<>
			<SectionHeader title={categoryName}>
				<Selector
					value={state.evCategoryVariantSort}
					onChange={dispatchSetEvCategoryVariantSort}
					placeholder="Sort"
					icon={SortAscIcon}
					options={EvCategoryVariantSortOptions} />
			</SectionHeader>

			<DataView
				forQuery={categoryVariants}
				getKey={x => x.productId}
				listViewHeaderLabels={["Product", "Market Price", "Short Printed"]}
				listViewHeaderClassNames={["min-w-0", "w-40 @md:w-52 @lg:w-64", "w-30 @lg:w-44"]}
				listComponent={EvCategoryVariantsListView}
				galleryComponent={EvCategoryVariantsGalleryView}
				additionalProps={additionalProps}
				scrollRef={scrollRef}
				galleryMinWidth={208} />
		</>
	);
};

interface EvCategoryVariantsViewProps extends EvItemBreakdownCategoryVariantResponseModel {
	onSelectCategoryVariant: (categoryVariant: EvItemBreakdownCategoryVariantResponseModel) => void;
}

const EvCategoryVariantsListView: FC<EvCategoryVariantsViewProps> = props => {
	const {
		marketPrice,
		shortPrintDescription,
		onSelectCategoryVariant
	} = props;

	const onClick = useCallback(() => onSelectCategoryVariant(props), [onSelectCategoryVariant, props]);

	return (
		<ListItem onClick={onClick}>
			<TableCell>
				<SmallProductDisplay {...props} />
			</TableCell>
			<TableCell>
				<MoneyDisplay money={marketPrice} mode="Standard" />
			</TableCell>
			<TableCell>
				{shortPrintDescription}
			</TableCell>
		</ListItem>
	);
};

const EvCategoryVariantsGalleryView: FC<EvCategoryVariantsViewProps> = props => {
	const {
		productName,
		fcId,
		imageUrl,
		marketPrice,
		shortPrintDescription,
		onSelectCategoryVariant
	} = props;

	const onClick = useCallback(() => onSelectCategoryVariant(props), [onSelectCategoryVariant, props]);

	return (
		<GalleryItem title={productName} description={fcId} imageUrl={imageUrl} onClick={onClick}>
			<GalleryItemProperty label="Market Price">
				<MoneyDisplay money={marketPrice} mode="Standard" />
			</GalleryItemProperty>
			{
				shortPrintDescription &&
				<GalleryItemProperty label="Short Printed">
					{shortPrintDescription}
				</GalleryItemProperty>
			}
		</GalleryItem>
	);
};
